import type { OperatorType } from "@client/api-services";
import { useRouteLoaderData } from "@remix-run/react";
import { useCallback } from "react";
import type { RootData } from "~/root";

export function useUrlGenerator() {
	const { siteId, imageVersionSettings: { countryImagesVersion, operatorImagesVersion, otherImagesVersion }, env: { IMAGES_HOST } } = useRouteLoaderData("root") as RootData;

	type GetImageUrlType = {
		(fileName: string): string;
		(fileName: string, siteSpecific: boolean): string;
		(fileName: string, folderName: string, siteSpecific?: boolean): string;
	};

	const getImageUrl = useCallback(
		function (fileName: string, foldernameOrSite?: string | boolean, siteSpecific?: boolean) {
			const folderName = typeof foldernameOrSite === "string" ? foldernameOrSite : "images";
			const imageSiteId = typeof foldernameOrSite === "boolean" && foldernameOrSite ? siteId : (siteSpecific ? siteId : undefined);
			if (imageSiteId)
				return `${IMAGES_HOST}/${folderName}/${imageSiteId}-${fileName}?v=${otherImagesVersion}`;
			else
				return `${IMAGES_HOST}/${folderName}/${fileName}?v=${otherImagesVersion}`;
		}, [IMAGES_HOST, otherImagesVersion, siteId]) as GetImageUrlType

	type GetOperatorImageUrlType = {
		(fileName: string | undefined): string;
		(operator: OperatorType | undefined): string;
	};

	const getOperatorImageUrl = useCallback(
		function (fileNameOrOperator: string | OperatorType | undefined): string {
			if (!fileNameOrOperator) return "";
			if (typeof fileNameOrOperator === "string") {
				const fileName = fileNameOrOperator;
				return `${IMAGES_HOST}/operators/${fileName}?v=${operatorImagesVersion}`;
			} else {
				const operator = fileNameOrOperator;
				if (operator.fileName) return `${IMAGES_HOST}/operators/${operator?.fileName}?v=${operatorImagesVersion}`;
				else return `${IMAGES_HOST}/operators/${operator?.operatorName.toLowerCase().replaceAll(" ", "-")}.png?v=${operatorImagesVersion}`;
			}
		}, [IMAGES_HOST, operatorImagesVersion]) as GetOperatorImageUrlType;

	const getCountryImageUrl = useCallback(function (countryCode?: string, size: "1x1" | "4x3" = "1x1") {
		if (!countryCode) return `${IMAGES_HOST}/flags/${size}/globe.svg?v=${countryImagesVersion}`;
		return `${IMAGES_HOST}/flags/${size}/${countryCode.toLowerCase()}.svg?v=${countryImagesVersion}`;
	}, [IMAGES_HOST, countryImagesVersion])

	const getCreditCardUrl = useCallback((schemeName: string) => {
		return `${IMAGES_HOST}/credit-cards/${schemeName.toLowerCase()}.svg`
	}, [IMAGES_HOST])

	return { getImageUrl, getOperatorImageUrl, getCountryImageUrl, getCreditCardUrl };
}

